import { c as _c } from "react/compiler-runtime";
import { useEffect } from 'react';
import { BROADCAST_CHANNEL, type BROADCAST_TOPIC } from '@src/utils/constants.util';
import { log } from '@src/utils/log.util';
const namespace = '[BroadcastChannel]';
type BroadcastMessage = {
  topic: BROADCAST_TOPIC;
  payload?: unknown;
};
type SubscriptionCallback = (payload?: unknown) => void;

// Global variables to be shared across all hook instances
let channel: BroadcastChannel | null = null;
const subscriptions = new Map<BROADCAST_TOPIC, SubscriptionCallback[]>();

/**
 * Custom hook for BroadcastChannel API
 * @returns {Object} - An object with publish and subscribe methods
 * @returns {Function} publish - Publishes a message to the channel
 * @returns {Function} subscribe - Subscribes to a topic and calls the callback when a message is received
 * @example
 * const { publish, subscribe } = useBroadcastChannel()
 * publish('topic ', { message: 'Hello, world!' })
 * subscribe('topic', (payload) => { console.log(payload) })
 */
export const useBroadcastChannel = () => {
  const $ = _c(2);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = [];
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  useEffect(_temp3, t0);
  const publish = _temp4;
  const subscribe = _temp5;
  let t1;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {
      publish,
      subscribe
    };
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  return t1;
};
function _temp(event) {
  const {
    topic,
    payload
  } = event.data;
  const callbacks = subscriptions.get(topic);
  callbacks?.forEach(callback => callback(payload));
  log.debug(`${namespace} Received message from topic: "${topic}"`);
}
function _temp2() {
  if (subscriptions.size === 0 && channel) {
    channel.close();
    channel = null;
  }
}
function _temp3() {
  if (!channel) {
    channel = new BroadcastChannel(BROADCAST_CHANNEL);
    const handleMessage = _temp;
    channel.addEventListener("message", handleMessage);
  }
  return _temp2;
}
function _temp4(topic_0, payload_0) {
  if (!channel) {
    return;
  }
  const message = {
    topic: topic_0,
    payload: payload_0
  };
  channel.postMessage(message);
  log.debug(`${namespace} Published message to topic: "${topic_0}"`, message);
}
function _temp5(topic_1, callback_0) {
  const callbacks_0 = subscriptions.get(topic_1) ?? [];
  subscriptions.set(topic_1, [...callbacks_0, callback_0]);
  return () => {
    const callbacks_1 = subscriptions.get(topic_1) ?? [];
    subscriptions.set(topic_1, callbacks_1.filter(cb => cb !== callback_0));
  };
}